import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { HERO } from "@/constants/testIds";
import { WEDDING } from "@/data/wedding";
import { useLang } from "@/i18n/LanguageContext";
import { OrchidFlourish, RoseFlourish } from "@/components/wedding/Flourish";

const useCountdown = (target) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds };
};

export const Hero = () => {
  const { t, lang } = useLang();
  const tt = useCountdown(WEDDING.date);

  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="relative overflow-hidden pt-28 sm:pt-36 pb-16 sm:pb-24"
    >
      <div className="absolute -top-32 -right-40 w-[680px] h-[680px] sunset-orb opacity-70 pointer-events-none" aria-hidden />
      <div className="absolute top-1/3 -left-32 w-[420px] h-[420px] rose-orb opacity-40 pointer-events-none" aria-hidden />
      <OrchidFlourish
        className="absolute -bottom-4 -right-8 w-[110px] sm:w-[160px] md:w-[200px] scale-x-[-1]"
        opacity={0.85}
      />
      <RoseFlourish
        className="absolute top-40 right-8 sm:top-56 sm:right-16 w-[80px] sm:w-[110px] md:w-[140px] rotate-[8deg] hidden md:block"
        opacity={0.85}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <motion.div
            className="flex items-center gap-4 mb-20 sm:mb-28 md:mb-32 flex-wrap"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <span className="font-overline text-[10px] text-black/60">{t("hero.saveTheDate")}</span>
            <span className="w-px h-3 bg-black/30" aria-hidden />
            <span className="font-overline text-[10px] tracking-[0.3em] text-[color:var(--rose-600)]">10 . 10 . 2026</span>
          </motion.div>

          <h1
            data-testid={HERO.names}
            className="font-display text-[64px] leading-[1.05] sm:text-[92px] md:text-[110px] lg:text-[136px] sm:leading-[1.02] tracking-[-0.04em] font-light"
          >
            <motion.span
              className="block italic will-change-transform"
              initial={{ opacity: 0, y: 40, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
              {WEDDING.brideFirst}
            </motion.span>
            <motion.span
              className="block pl-8 sm:pl-16 text-[color:var(--sunset-500)] font-normal will-change-transform"
              initial={{ opacity: 0, y: 40, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 1.1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="italic mr-4 sm:mr-6 opacity-80">&amp;</span>
              {WEDDING.groomFirst}
            </motion.span>
          </h1>

          <motion.div
            className="mt-10 flex flex-wrap items-baseline gap-x-8 gap-y-3"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
          >
            <div data-testid={HERO.date} className="font-serif text-2xl sm:text-3xl italic">
              {t("hero.dateLabel")}, {t("hero.yearLabel")}
            </div>
            <div data-testid={HERO.location} className="font-overline text-[11px] text-black/70">
              {t("hero.location")}
            </div>
          </motion.div>
        </div>

        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, scale: 0.96, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_bride-groom-hub-75/artifacts/dy1di8vq_CLP08443.webp"
              alt="Carmen & Kerbert at sunset"
              className="img-art w-full aspect-[4/5] object-cover"
            />
            <a
              href="https://instagram.com/chlaaneste"
              target="_blank"
              rel="noreferrer"
              data-testid="hero-photo-credit"
              className="absolute bottom-3 right-3 font-serif italic text-[11px] sm:text-xs text-white/90 hover:text-white transition-colors [text-shadow:0_1px_6px_rgba(0,0,0,0.5)]"
            >
              @chlaaneste
            </a>
          </div>
        </motion.div>
      </div>

      <div
        data-testid={HERO.countdown}
        className="relative max-w-7xl mx-auto px-6 sm:px-10 mt-20 sm:mt-28 reveal reveal-delay-3"
      >
        <div className="hairline mb-6" />
        <div className="grid grid-cols-4 gap-4 sm:gap-12">
          {[
            { label: t("hero.countdown.days"), v: tt.days },
            { label: t("hero.countdown.hours"), v: tt.hours },
            { label: t("hero.countdown.minutes"), v: tt.minutes },
            { label: t("hero.countdown.seconds"), v: tt.seconds },
          ].map((unit, i) => (
            <div key={i} className="flex flex-col">
              <div className="font-display text-4xl sm:text-6xl lg:text-7xl tracking-tighter">
                {String(unit.v).padStart(2, "0")}
              </div>
              <div className="font-overline text-[10px] text-black/60 mt-2">{unit.label}</div>
            </div>
          ))}
        </div>
        <div className="hairline mt-6" />
      </div>

      <div className="relative mt-16 sm:mt-24 overflow-hidden">
        <div className="marquee-track flex gap-12 whitespace-nowrap will-change-transform">
          {Array.from({ length: 2 }).map((_, j) => (
            <div key={`${lang}-${j}`} className="flex items-center gap-12">
              {(t("hero.marquee") || []).map((s, i) => (
                <span
                  key={`${j}-${i}`}
                  className="font-serif italic text-3xl sm:text-5xl text-black/80 flex items-center gap-12"
                >
                  {s}
                  <span className="w-2 h-2 rounded-full bg-[color:var(--sunset-500)]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
